import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Building2, Calendar, Target } from 'lucide-react';
import { getCompanyById } from '../data/companies';

export const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = getCompanyById(id);

  if (!company) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Company not found</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sky-400 hover:text-sky-300 transition-colors"
          >
            Return to Portfolio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid={`company-detail-${id}`}>
      {/* Hero Section */}
      <div className="relative h-[40vh] sm:h-[50vh] lg:h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 detail-hero"
          style={{ backgroundImage: `url(${company.image})` }}
        />
        <div className="absolute inset-0 detail-overlay" />
        
        {/* Back Button */}
        <motion.button
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          onClick={() => navigate('/dashboard')}
          className="absolute top-4 left-4 sm:top-8 sm:left-8 z-20 flex items-center gap-2 text-zinc-400 hover:text-white transition-colors group"
          data-testid="back-to-portfolio"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium hidden sm:inline">Back to Portfolio</span>
        </motion.button>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 lg:p-16 z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-sky-400 mb-2 sm:mb-3 block">
              {company.category}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-2 sm:mb-4">
              {company.name}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-zinc-300 font-light max-w-2xl">
              {company.tagline}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-8 sm:py-12 lg:py-16">
        {/* Stats Row */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-16"
        >
          <div className="glass-card rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2 sm:mb-3">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-sky-400" />
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Founded</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-white">{company.stats.founded}</p>
          </div>
          <div className="glass-card rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2 sm:mb-3">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-sky-400" />
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Focus</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-white">{company.stats.focus}</p>
          </div>
          <div className="glass-card rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2 sm:mb-3">
              <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-sky-400" />
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Specialty</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-white">{company.stats.specialty}</p>
          </div>
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mb-8 sm:mb-16"
        >
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4 sm:mb-6">
            About
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-zinc-300 font-light leading-relaxed mb-6 sm:mb-8">
            {company.description}
          </p>
          <p className="text-base sm:text-lg md:text-xl text-zinc-400 font-light leading-relaxed">
            {company.philosophy}
          </p>
        </motion.div>

        {/* Services */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mb-8 sm:mb-16"
        >
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4 sm:mb-6">
            Services & Capabilities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {company.services.map((service, index) => (
              <motion.div
                key={service}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
                className="glass-card rounded-lg p-3 sm:p-4 flex items-center gap-3"
              >
                <div className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0" />
                <span className="text-zinc-300 font-medium text-sm sm:text-base">{service}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <a
            href={`https://${company.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-sky-500 hover:bg-sky-400 text-black font-semibold rounded-lg transition-colors text-sm sm:text-base"
            data-testid="visit-website"
          >
            Visit Website
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 border border-white/10 hover:border-white/20 text-white font-medium rounded-lg transition-colors text-sm sm:text-base"
            data-testid="view-portfolio"
          >
            View Full Portfolio
          </button>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 sm:py-8 px-4 sm:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-zinc-600">
            A Waterline Group Company
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-zinc-500 hover:text-sky-400 transition-colors"
          >
            ← Return to Portfolio
          </button>
        </div>
      </footer>
    </div>
  );
};
